/**
 * ライブアクティビティフィード（タスク54）。公開中パネルの真下にトーストを流す。
 *
 * 見た目と動きは指示書のモックに合わせた（モックは削除済み・コミット 7c1774b の docs/reinvent-live-activity-feed-mock.html）:
 * - 同時に出すのは MAX_VISIBLE 件まで。超えた分はキューに積んで順に出す
 * - 1件は TOAST_MS で消える。新着は一番上に入り、既存は下へスライドして押し下げられる（FLIP）
 * - 退場は下へ流れながらフェード。動きを減らす設定ではフェードのみ（CSS 側）
 * - 公開中パネルを開いている間（paused）は出さない。閉じたらキューの続きから出す
 * - OFF にしたら表示中もキューも捨てる
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LocationsResponse, PostsResponse } from '../api/client';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { VENUES } from '../map/venues';
import type { FeedEvent } from './detectEvents';
import { useActivityFeed } from './useActivityFeed';
import './activityFeed.css';

const MAX_VISIBLE = 3;
const TOAST_MS = 4000;
/** 退場アニメーションの長さ（CSS の 0.45s）。transitionend が来ない場合の後始末に使う。 */
const LEAVE_MS = 450;
/** キューの上限。パネルを長く開いていた後に古い通知がまとめて出ないよう、古いものから捨てる。 */
const MAX_QUEUE = 6;

const ICONS: Record<FeedEvent['type'], string> = {
  checkin: '🟢',
  post: '📸',
  move: '📡',
  agg: '📡',
};

interface Item {
  event: FeedEvent;
  phase: 'enter' | 'in' | 'leaving';
}

function venueName(id: string | undefined, lang: Lang): string | undefined {
  const v = id ? VENUES.find((venue) => venue.id === id) : undefined;
  if (!v) {
    return undefined;
  }
  return lang === 'ja' ? v.name : v.nameEn;
}

function ToastBody({ event, lang }: { event: FeedEvent; lang: Lang }) {
  const m = messages(lang).feed;
  let head: JSX.Element;
  let meta: string | undefined;
  switch (event.type) {
    case 'checkin':
      head = (
        <>
          <b>{event.name}</b> {m.checkin(venueName(event.venueId, lang))}
        </>
      );
      meta = messages(lang).time.justNow;
      break;
    case 'post': {
      const venue = venueName(event.venueId, lang);
      head = (
        <>
          <b>{event.name}</b> {m.posted}
        </>
      );
      meta = venue ? m.near(venue) : undefined;
      break;
    }
    case 'move': {
      const from = venueName(event.fromVenueId, lang);
      const to = venueName(event.toVenueId, lang);
      head = (
        <>
          <b>{event.name}</b> {m.move}
        </>
      );
      if (from && to && from !== to) {
        meta = `${from} → ${to}`;
      } else if (to) {
        meta = `→ ${to}`;
      } else if (from) {
        meta = `${from} →`;
      }
      break;
    }
    case 'agg': {
      const names = event.venueIds
        .map((id) => venueName(id, lang))
        .filter((n): n is string => n !== undefined);
      head = (
        <>
          <b>{m.aggCount(event.count)}</b> {m.aggSuffix}
        </>
      );
      meta = names.length > 0 ? m.othersAfter(names.join(m.venueSep)) : undefined;
      break;
    }
  }
  return (
    <div className="activity-feed__text">
      {head}
      {meta && <div className="activity-feed__meta">{meta}</div>}
    </div>
  );
}

export interface ActivityFeedProps {
  readonly pins: LocationsResponse | null;
  readonly posts: PostsResponse | null;
  /** フィードの ON/OFF（右上ツールバーの 📡）。 */
  readonly enabled: boolean;
  /** 公開中パネルを開いている間 true（出さない）。 */
  readonly paused: boolean;
  readonly lang: Lang;
}

export function ActivityFeed({
  pins,
  posts,
  enabled,
  paused,
  lang,
}: ActivityFeedProps) {
  const [items, setItems] = useState<Item[]>([]);
  /** キューへの追加を表示側に知らせるための刻み。 */
  const [queueTick, setQueueTick] = useState(0);
  const queueRef = useRef<FeedEvent[]>([]);
  const elsRef = useRef(new Map<number, HTMLDivElement>());
  /** FLIP の First（挿入前の各トーストの top）。挿入直後のレイアウトで使って消す。 */
  const prevTopsRef = useRef<Map<number, number> | null>(null);
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>());

  const later = useCallback((fn: () => void, ms: number) => {
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      fn();
    }, ms);
    timersRef.current.add(timer);
  }, []);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((it) => it.event.id !== id));
  }, []);

  useActivityFeed(pins, posts, (events) => {
    if (!enabled) {
      return;
    }
    const queue = queueRef.current;
    queue.push(...events);
    if (queue.length > MAX_QUEUE) {
      queue.splice(0, queue.length - MAX_QUEUE);
    }
    setQueueTick((n) => n + 1);
  });

  // OFF にしたら表示中もキューも捨てる
  useEffect(() => {
    if (enabled) {
      return;
    }
    queueRef.current = [];
    for (const timer of timersRef.current) {
      clearTimeout(timer);
    }
    timersRef.current.clear();
    setItems([]);
  }, [enabled]);

  // アンマウント時にタイマーを止める
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
    };
  }, []);

  // 空きがあればキューから1件出す（出すたびに items が変わってもう一度ここに来るので、空きの分だけ続けて出る）
  useEffect(() => {
    if (!enabled || paused || items.length >= MAX_VISIBLE) {
      return;
    }
    const event = queueRef.current.shift();
    if (!event) {
      return;
    }
    const tops = new Map<number, number>();
    for (const [id, el] of elsRef.current) {
      tops.set(id, el.getBoundingClientRect().top);
    }
    prevTopsRef.current = tops;
    setItems((prev) => [{ event, phase: 'enter' }, ...prev]);
    later(() => {
      setItems((prev) =>
        prev.map((it) =>
          it.event.id === event.id ? { ...it, phase: 'leaving' } : it,
        ),
      );
      // transitionend が来ない場合（パネルを開いて display:none の間など）の後始末
      later(() => remove(event.id), LEAVE_MS + 150);
    }, TOAST_MS);
  }, [items, enabled, paused, queueTick, later, remove]);

  // FLIP: 既存のトーストを挿入前の位置に transform で戻し、次のフレームで外して新しい位置へ滑らせる。
  // 新着は enter（上にずれて透明）で描いてから in にして降ろす。
  useLayoutEffect(() => {
    const tops = prevTopsRef.current;
    if (!tops) {
      return;
    }
    prevTopsRef.current = null;
    const moved: HTMLDivElement[] = [];
    for (const [id, top] of tops) {
      const el = elsRef.current.get(id);
      if (!el) {
        continue;
      }
      const delta = top - el.getBoundingClientRect().top;
      if (delta !== 0) {
        el.style.transition = 'none';
        el.style.transform = `translateY(${delta}px)`;
        moved.push(el);
      }
    }
    const enteringId = items[0]?.event.id;
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        for (const el of moved) {
          el.style.transition = '';
          el.style.transform = '';
        }
        setItems((prev) =>
          prev.map((it) =>
            it.event.id === enteringId && it.phase === 'enter'
              ? { ...it, phase: 'in' }
              : it,
          ),
        );
      }),
    );
  }, [items]);

  const m = messages(lang).feed;

  return (
    <div
      className={`activity-feed${paused ? ' activity-feed--paused' : ''}`}
      aria-live="polite"
      aria-label={m.label}
    >
      {items.map(({ event, phase }) => (
        <div
          key={event.id}
          ref={(el) => {
            if (el) {
              elsRef.current.set(event.id, el);
            } else {
              elsRef.current.delete(event.id);
            }
          }}
          className={`activity-feed__toast activity-feed__toast--${event.type}${
            phase === 'enter'
              ? ' activity-feed__toast--enter'
              : phase === 'leaving'
                ? ' activity-feed__toast--leaving'
                : ''
          }`}
          onTransitionEnd={(e) => {
            if (phase === 'leaving' && e.propertyName === 'opacity') {
              remove(event.id);
            }
          }}
        >
          <div className="activity-feed__icon" aria-hidden="true">
            {ICONS[event.type]}
          </div>
          <ToastBody event={event} lang={lang} />
        </div>
      ))}
    </div>
  );
}
